import React, { useState, useEffect } from "react";
import "../css/payments.css";

function PaymentStats() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [actionLoading, setActionLoading] = useState({});
  const [filters, setFilters] = useState({
    offerConfirmed: null, // null = all, true = confirmed, false = not confirmed
    completionConfirmed: null, // null = all, true = confirmed, false = not confirmed
    reportApproval: null // null = all, true = approved, false = rejected/pending
  });

  // Helper function for authenticated requests
  const authFetch = async (url, options = {}) => {
    const token = localStorage.getItem("access_token");
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers
    };
    return fetch(url, { ...options, headers });
  };

  useEffect(() => {
    const fetchPaymentData = async () => {
      setLoading(true);
      setError("");
      
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          setError("No authentication token found");
          setLoading(false);
          return;
        }

        const response = await authFetch("/api/admin/student_payments/offer");
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || "Failed to fetch payment data");
        }

        const data = await response.json();
        setPayments(data);

      } catch (error) {
        console.error("Error fetching payment data:", error);
        setError(error.message || "Failed to load payment data");
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentData();
  }, []);

  // Handle confirm payment
  const handleConfirmPayment = async (sip_id) => {
    setActionLoading(prev => ({ ...prev, [sip_id]: 'confirm' }));
    try {
      const response = await authFetch(`/api/admin/student_payments/offer/${sip_id}`, {
        method: "POST"
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to confirm payment");
      }

      // Update local state
      setPayments(prev => prev.map(p => 
        p.sip_id === sip_id ? { ...p, offer_payment_conf: true } : p
      ));

    } catch (error) {
      console.error("Error confirming payment:", error);
      alert(error.message);
    } finally {
      setActionLoading(prev => ({ ...prev, [sip_id]: null }));
    }
  };

  // Handle unconfirm payment
  const handleUnconfirmPayment = async (sip_id) => {
    setActionLoading(prev => ({ ...prev, [sip_id]: 'unconfirm' }));
    try {
      const response = await authFetch(`/api/admin/student_payments/offer/${sip_id}`, {
        method: "DELETE"
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to unconfirm payment");
      }

      // Update local state
      setPayments(prev => prev.map(p => 
        p.sip_id === sip_id ? { ...p, offer_payment_conf: false } : p
      ));

    } catch (error) {
      console.error("Error unconfirming payment:", error);
      alert(error.message);
    } finally {
      setActionLoading(prev => ({ ...prev, [sip_id]: null }));
    }
  };

  // Handle mark completion
  const handleMarkCompletion = async (sip_id) => {
    setActionLoading(prev => ({ ...prev, [sip_id]: 'complete' }));
    try {
      const response = await authFetch(`/api/admin/student/mark_completion`, {
        method: "POST",
        body: JSON.stringify({ sip_id })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to mark completion");
      }

      // Update local state
      setPayments(prev => prev.map(p => 
        p.sip_id === sip_id ? { ...p, completion_conf: true } : p
      ));

    } catch (error) {
      console.error("Error marking completion:", error);
      alert(error.message);
    } finally {
      setActionLoading(prev => ({ ...prev, [sip_id]: null }));
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge class
  const getStatusBadge = (status) => {
    if (!status) return "status-badge pending";
    switch(status.toLowerCase()) {
      case 'success':
        return "status-badge success";
      case 'failed':
        return "status-badge failed";
      case 'pending':
        return "status-badge pending";
      default:
        return "status-badge pending";
    }
  };

  // Get confirmation badge
  const getConfirmationBadge = (value) => {
    if (value === true || value === 1 || value === "true") {
      return <span className="confirmation-badge confirmed">✓ Confirmed</span>;
    } else if (value === false || value === 0 || value === "false") {
      return <span className="confirmation-badge not-confirmed">✗ Not Confirmed</span>;
    } else {
      return <span className="confirmation-badge pending">—</span>;
    }
  };

  // Get project report approval badge
  const getReportApprovalBadge = (value) => {
    if (value === true) {
      return <span className="report-badge approved">✓ Approved</span>;
    } else if (value === false) {
      return <span className="report-badge rejected">✗ Yet to approve</span>;
    } else if (value === null || value === undefined) {
      return <span className="report-badge not-submitted">📄 Not Submitted</span>;
    } else {
      return <span className="report-badge pending">⏳ Pending</span>;
    }
  };

  // Handle filter change
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType] === value ? null : value
    }));
  };

  // Apply filters to payments
  const getFilteredPayments = () => {
    let filtered = payments;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(payment => 
        payment.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.sip_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply offer confirmed filter
    if (filters.offerConfirmed !== null) {
      filtered = filtered.filter(p => p.offer_payment_conf === filters.offerConfirmed);
    }

    // Apply completion confirmed filter
    if (filters.completionConfirmed !== null) {
      filtered = filtered.filter(p => p.completion_conf === filters.completionConfirmed);
    }

    // Apply report approval filter
    if (filters.reportApproval !== null) {
      filtered = filtered.filter(p => p.project_report_approval === filters.reportApproval);
    }

    return filtered;
  };

  const filteredPayments = getFilteredPayments();

  // Check if any filter is active
  const hasActiveFilters = filters.offerConfirmed !== null || filters.completionConfirmed !== null || filters.reportApproval !== null;

  if (loading) {
    return (
      <div className="payment-stats loading">
        <h2>Student Payments Overview</h2>
        <div className="loading-spinner">Loading payment data...</div>
      </div>
    );
  }

  if (error) {
    return (
      
      <div className="payment-stats error">
        <h2>Student Payments Overview</h2>
        <div className="error-message">
          <p>Error: {error}</p>
          <button onClick={() => window.location.reload()} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="header-section">
        <h2>Student Payments Overview</h2>
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by name, SIP ID, email, or transaction ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="filters-bar">
        <div className="filter-group">
          <span className="filter-label">Offer Payment:</span>
          <button 
            className={`filter-btn ${filters.offerConfirmed === true ? 'active' : ''}`}
            onClick={() => handleFilterChange('offerConfirmed', true)}
          >
            Confirmed
          </button>
          <button 
            className={`filter-btn ${filters.offerConfirmed === false ? 'active' : ''}`}
            onClick={() => handleFilterChange('offerConfirmed', false)}
          >
            Not Confirmed
          </button>
        </div>

        <div className="filter-group">
          <span className="filter-label">Completion:</span>
          <button 
            className={`filter-btn ${filters.completionConfirmed === true ? 'active' : ''}`}
            onClick={() => handleFilterChange('completionConfirmed', true)}
          >
            Confirmed
          </button>
          <button 
            className={`filter-btn ${filters.completionConfirmed === false ? 'active' : ''}`}
            onClick={() => handleFilterChange('completionConfirmed', false)}
          >
            Not Confirmed
          </button>
        </div>

        <div className="filter-group">
          <span className="filter-label">Report Approval:</span>
          <button 
            className={`filter-btn ${filters.reportApproval === true ? 'active' : ''}`}
            onClick={() => handleFilterChange('reportApproval', true)}
          >
            Approved
          </button>
          <button 
            className={`filter-btn ${filters.reportApproval === false ? 'active' : ''}`}
            onClick={() => handleFilterChange('reportApproval', false)}
          >
            Not Approved
          </button>
        </div>

        {hasActiveFilters && (
          <button 
            className="filter-btn clear"
            onClick={() => setFilters({
              offerConfirmed: null,
              completionConfirmed: null,
              reportApproval: null
            })}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Summary Stats */}
      {/* <div className="summary-section">
        <div className="stat-item">
          <span className="stat-label">Total Students:</span>
          <span className="stat-value">{payments.length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Offer Confirmed:</span>
          <span className="stat-value success">{payments.filter(p => p.offer_payment_conf === true).length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Offer Not Confirmed:</span>
          <span className="stat-value warning">{payments.filter(p => p.offer_payment_conf === false).length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Completion Confirmed:</span>
          <span className="stat-value success">{payments.filter(p => p.completion_conf === true).length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Completion Not Confirmed:</span>
          <span className="stat-value warning">{payments.filter(p => p.completion_conf === false).length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Report Approved:</span>
          <span className="stat-value success">{payments.filter(p => p.project_report_approval === true).length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Report Not Approved:</span>
          <span className="stat-value warning">{payments.filter(p => p.project_report_approval === false).length}</span>
        </div>
        {searchTerm && (
          <div className="stat-item">
            <span className="stat-label">Showing:</span>
            <span className="stat-value info">{filteredPayments.length}</span>
          </div>
        )}
      </div> */}

      {/* Students Table */}
      {filteredPayments.length > 0 ? (
        <div className="table-container">
          <table className="students-table">
            <thead>
              <tr>
                <th>SIP ID</th>
                <th>Student Name</th>
                <th>Email</th>
                <th>Transaction ID</th>
                <th>Amount</th>
                <th>Payment Status</th>
                <th>Payment Date</th>
                <th>Offer Payment</th>
                <th>Completion</th>
                <th>Report Approval</th> {/* New column */}
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((payment, index) => (
                <tr key={payment.sip_id || index}>
                  <td>
                    <span className="sip-id-badge">{payment.sip_id || "N/A"}</span>
                  </td>
                  <td>
                    <strong>{payment.name || "N/A"}</strong>
                  </td>
                  <td>
                    <a href={`mailto:${payment.email}`} className="email-link">
                      {payment.email || "N/A"}
                    </a>
                  </td>
                  <td>
                    {payment.transaction_id ? (
                      <span className="transaction-id" title={payment.transaction_id}>
                        {payment.transaction_id}
                      </span>
                    ) : (
                      <span className="no-data">—</span>
                    )}
                  </td>
                  <td>
                    {payment.amount ? (
                      <span className="amount">{formatCurrency(payment.amount)}</span>
                    ) : (
                      <span className="no-data">—</span>
                    )}
                  </td>
                  <td>
                    {payment.status ? (
                      <span className={getStatusBadge(payment.status)}>
                        {payment.status}
                      </span>
                    ) : (
                      <span className="no-data">—</span>
                    )}
                  </td>
                  <td>
                    {payment.order_date ? formatDate(payment.order_date) : "—"}
                  </td>
                  <td className="confirmation-cell">
                    <div className="confirmation-with-action">
                      {getConfirmationBadge(payment.offer_payment_conf)}
                      {payment.offer_payment_conf ? (
                        <button
                          onClick={() => handleUnconfirmPayment(payment.sip_id)}
                          disabled={actionLoading[payment.sip_id] === 'unconfirm'}
                          className="small-action-btn unconfirm-btn"
                          title="Unconfirm Payment"
                        >
                          {actionLoading[payment.sip_id] === 'unconfirm' ? (
                            <span className="loading-spinner-small"></span>
                          ) : (
                            'Undo'
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleConfirmPayment(payment.sip_id)}
                          disabled={actionLoading[payment.sip_id] === 'confirm'}
                          className="small-action-btn confirm-btn"
                          title="Confirm Payment"
                        >
                          {actionLoading[payment.sip_id] === 'confirm' ? (
                            <span className="loading-spinner-small"></span>
                          ) : (
                            'Confirm'
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="confirmation-cell">
                    <div className="confirmation-with-action">
                      {getConfirmationBadge(payment.completion_conf)}
                      {!payment.completion_conf && (
                        <button
                          onClick={() => handleMarkCompletion(payment.sip_id)}
                          disabled={actionLoading[payment.sip_id] === 'complete'}
                          className="small-action-btn complete-btn"
                          title="Mark as Completed"
                        >
                          {actionLoading[payment.sip_id] === 'complete' ? (
                            <span className="loading-spinner-small"></span>
                          ) : (
                            'Complete'
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                  {/* New column for project report approval */}
                  <td className="report-cell">
                    {getReportApprovalBadge(payment.project_report_approval)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-data">
          {searchTerm || hasActiveFilters ? (
            <p>No students found matching your criteria.</p>
          ) : (
            <p>No student payment data found.</p>
          )}
        </div>
      )}

    </>
  );
}

export default PaymentStats;